import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  newPerformanceDataQuery,
  newPerformancePageQuery,
  type NewPerformanceData,
  type StandingRow,
} from '../../api/easeedeskNewPerformance'
import { Content } from '../../ui/Shell'
import { BarChart, COLORS, LineChart, PieChart } from './MyPerformanceCharts'
import { RANGES, rangeOf, type RangeName } from './range'

/**
 * Tabloul "My new Performance" (LTP) -
 * `application/controllers/easeedesk-dashboards/My_new_performance.php`,
 * machetele `views/version-3/dashboards/easeedesk/my_new_performance/*`.
 *
 * E al doisprezecelea si ultimul tablou Easeedesk mutat, si cel mai tarziu:
 * cifrele lui vin din `company_reports/Performance`, un model de 2186 de
 * randuri in care scara de puncte e scrisa de 14 ori, in SQL, cu furnizorul
 * din sesiune lipit inauntru. Scara sta acum intr-un singur loc, in API
 * (`PointScheme`), si furnizorul se leaga ca parametru.
 *
 * ZIUA NU INCEPE PESTE TOT LA MIEZUL NOPTII. La furnizorul 20266 tura tine de
 * la 12 la 12, si perioada de pornire a ecranului o urmeaza - exact ca in
 * scriptul vechi, care se uita la variabila `sp` scrisa de antetul portalului.
 * API-ul spune ora, ecranul o foloseste.
 *
 * NU E BIFA "All Active Tickets". In macheta veche e comentata, iar scriptul o
 * intreaba mai departe pe un element care nu exista - deci perioada se aplica
 * mereu. Aici se aplica mereu, pe fata.
 */

const STYLE = `
.od-ltp .panel { margin-bottom: 20px; }
.od-ltp .align-center { text-align: center; }
.od-ltp .color-success { color: #5cb85c; }
.od-ltp .color-danger { color: #d9534f; }
.od-ltp .bg-primary { background-color: #337ab7; color: #fff; }
.od-ltp .standing { display: flex; align-items: baseline; gap: 12px; padding: 4px 0; }
.od-ltp .standing .rank { width: 32px; font-size: 26px; margin: 0; }
.od-ltp .standing .who { flex: 1; }
.od-ltp .standing.me { color: #000; font-weight: bold; }
.od-ltp .big { font-size: 40px; margin: 0; }
.od-ltp .cards { display: flex; flex-wrap: wrap; }
.od-ltp .cards > div { flex: 1 1 14%; min-width: 120px; }
`

const EMPTY: NewPerformanceData = {
  ready: false,
  service_provider: { sp: 0 },
  live_points: {
    ltp: 0,
    connectedtickets: 0,
    over20minInConnected: 0,
    over20minInRemoteControl: 0,
    over10minInProgress: 0,
    latetotookover: 0,
  },
  resolved_points: { ltp_resolved: 0, rework: 0 },
  topresolved: [],
  admin: { login: null, logout: null, inactiveMinutes: 0, workingSeconds: 0, pauseSeconds: 0 },
  category_tickets: { extended: 0, advanced: 0, basics: 0 },
  standings: { live: [], resolved: [] },
  active: 0,
  closed: 0,
  top: [],
  active_daily: [],
}

/**
 * Durata scurta a portalului: `seconds2humanShort` din `time_helper.php`,
 * trecuta prin `str_replace(' ', '')` in controller - "3h20m15s". Zero, sau
 * mai putin, se scrie "0s", ca acolo.
 */
function short(seconds: number): string {
  if (seconds <= 0) {
    return '0s'
  }

  const parts: string[] = []
  const days = Math.floor((seconds % 2592000) / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const rest = seconds % 60
  const months = Math.floor(seconds / 2592000)

  if (months > 0) parts.push(`${months}M`)
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  if (rest > 0) parts.push(`${rest}s`)

  return parts.join('')
}

/**
 * Perioada, mutata pe ora la care incepe ziua la furnizorul asta.
 *
 * Scriptul vechi cerea `[azi HH:00:00, maine HH:00:00)`, si la fel pentru o
 * perioada aleasa: capatul de sus e ZIUA DE DUPA ultima aleasa, la aceeasi
 * ora. Cu `hour = 0` iese fereastra obisnuita de zi.
 */
function shift(name: RangeName, hour: number): { from: string; to: string } {
  const base = rangeOf(name)
  const clock = ` ${String(hour).padStart(2, '0')}:00:00`
  const day = (text: string, plus: number) => {
    const d = new Date(`${text.slice(0, 10)}T00:00:00`)
    d.setDate(d.getDate() + plus)

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }

  return { from: day(base.from, 0) + clock, to: day(base.to, 1) + clock }
}

/**
 * Un panou "My Position In Team": cinci randuri, cu al meu scris in negru
 * intre celelalte - exact ca in macheta, unde randul din mijloc are
 * `color: black` si restul alb.
 */
function Standing({ title, badge, rows }: { title: string; badge: string; rows: StandingRow[] }) {
  return (
    <div className="panel bg-primary">
      <div className="panel-heading">
        <h4 style={{ display: 'inline-block' }}>{title}</h4>
        <h4 className="pull-right" style={{ display: 'inline-block', marginRight: 15 }}>
          {badge}
        </h4>
      </div>
      <div className="panel-body">
        {rows.length === 0 ? <div>Not available</div> : null}
        {rows.map((one) => (
          <div key={one.position} className={one.me ? 'standing me' : 'standing'}>
            <h1 className="rank">{one.position}</h1>
            <span className="who">{one.name}</span>
            <span>{one.pct}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Card({ title, value, green }: { title: string; value: number; green?: boolean }) {
  return (
    <div className="align-center">
      <h4 className={green ? 'color-success' : undefined}>{title}</h4>
      <h1 className="big" style={green ? { color: 'green' } : undefined}>
        {value}
      </h1>
    </div>
  )
}

export function NewPerformancePage() {
  const [rangeName, setRangeName] = useState<RangeName>('Today')

  const page = useQuery(newPerformancePageQuery).data
  const window = shift(rangeName, page?.day_start_hour ?? 0)
  const data = useQuery(newPerformanceDataQuery(window.from, window.to)).data ?? EMPTY
  const live = data.live_points
  const closed = data.resolved_points
  const activity = data.admin
  const category = data.category_tickets

  return (
    <Content>
      <style>{STYLE}</style>
      <div className="od-ltp">
        <div className="row mb-5">
          <div className="col-md-9">
            <h1>Live Dashboard</h1>
          </div>
          <div className="col-md-3">
            <select
              id="reportrange"
              className="form-control pull-right"
              style={{ width: 'auto' }}
              value={rangeName}
              onChange={(e) => setRangeName(e.target.value as RangeName)}
            >
              {RANGES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {page && !page.ready ? (
          <div className="panel panel-default">
            <div className="panel-body">
              This dashboard scores tickets with a point scale that exists only for two service providers. Yours has none, so there is
              nothing to count.
            </div>
          </div>
        ) : null}

        <div className="panel panel-default">
          <div className="panel-body">
            <div className="cards">
              <Card title="Live Tickets Points" value={live.ltp} green />
              <Card title="Active" value={data.active} />
              <Card title="Connected" value={live.connectedtickets} />
              <Card title="Late Time Connected" value={live.over20minInConnected} />
              <Card title="Late to Control" value={live.over20minInRemoteControl} />
              <Card title="Late to In Progress" value={live.over10minInProgress} />
              <Card title="Late to Tookover" value={live.latetotookover} />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <Standing title="My Position In Team" badge="LTP" rows={data.standings.live} />
          </div>

          <div className="col-md-4">
            <div className="panel panel-default">
              <div className="panel-heading bg-white">
                <h4>Top Ticket Status</h4>
              </div>
              <div className="panel-body" style={{ height: 300 }}>
                <BarChart
                  label="# user active tickets"
                  bars={data.top.map((one) => ({
                    label: one.status_name,
                    value: one.count,
                    color: one.status_color,
                  }))}
                />
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="panel panel-default">
              <div className="panel-heading bg-white">
                <h4>Active / Closed</h4>
              </div>
              <div className="panel-body" style={{ height: 300 }}>
                <PieChart
                  title="Active / Closed"
                  slices={[
                    { label: 'Active', value: data.active, color: COLORS.red },
                    { label: 'Closed', value: data.closed, color: COLORS.blue },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>

        <h3>Closed Dashboard</h3>

        <div className="panel panel-default">
          <div className="panel-body">
            <div className="row">
              <div className="col-md-6 align-center">
                <h4 className="color-success">Ticket Points</h4>
                <h1 className="big">{closed.ltp_resolved}</h1>
              </div>
              <div className="col-md-6 align-center">
                <h4 className="color-danger">Reopen</h4>
                <h1 className="big">{closed.rework}</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <Standing title="My Position In Team" badge="TP" rows={data.standings.resolved} />
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <div className="panel panel-default">
              <div className="panel-heading bg-white">
                <h4>Top Ticket Types</h4>
              </div>
              <div className="panel-body" style={{ height: 300 }}>
                <BarChart
                  label="# user resolved tickets"
                  bars={data.topresolved.map((one) => ({
                    label: one.type_name,
                    value: one.count,
                    color: one.color_identifier,
                  }))}
                />
              </div>
            </div>
          </div>

          {/*
            * "Category Tickets Number" nu se vedea in portal: citirea da un rand
            * cu `extended` / `advanced` / `basics`, iar scriptul cere de la el
            * `count`, `type_name` si `color_identifier` - trei campuri care nu
            * exista, deci graficul iesea gol. Aici sunt cele trei cifre chiar
            * asa cum le da citirea.
            */}
          <div className="col-md-4">
            <div className="panel panel-default">
              <div className="panel-heading bg-white">
                <h4>Category Tickets Number</h4>
              </div>
              <div className="panel-body" style={{ height: 300 }}>
                <BarChart
                  label="# user resolved tickets"
                  bars={[
                    { label: 'Basics', value: category.basics, color: '#5cb85c' },
                    { label: 'Advanced', value: category.advanced, color: '#337ab7' },
                    { label: 'Extended', value: category.extended, color: '#d9534f' },
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="panel panel-default">
              <div className="panel-heading bg-white">
                <h4>Activity</h4>
              </div>
              <div className="panel-body" style={{ height: 300 }}>
                <div>
                  Working Time: <span style={{ color: 'green' }}>{short(activity.workingSeconds)}</span>
                </div>
                <div>
                  Pause Time: <span style={{ color: 'red' }}>{short(activity.pauseSeconds)}</span>
                </div>
                <div>
                  Log In: <span style={{ color: 'green' }}>{activity.login ?? 'Not Available'}</span>
                </div>
                <div>
                  Log Out: <span style={{ color: 'red' }}>{activity.logout ?? 'Not Available'}</span>
                </div>
                <div>
                  Status:{' '}
                  {activity.inactiveMinutes < 15 ? (
                    <span className="label label-success">Online</span>
                  ) : (
                    <span className="label label-danger">Inactive</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-8">
            <div className="panel panel-default">
              <div className="panel-heading bg-white">
                <h4>Tickets / hours</h4>
              </div>
              <div className="panel-body" style={{ height: 300 }}>
                <LineChart
                  labels={data.active_daily.map((one) => one.created)}
                  series={[
                    { label: 'Active Tickets', color: 'rgb(255, 99, 132)', values: data.active_daily.map((o) => o.active) },
                    { label: 'Progress Tickets', color: 'rgb(54, 162, 235)', values: data.active_daily.map((o) => o.progress) },
                    { label: 'Resolved Tickets', color: 'rgb(87, 255, 60)', values: data.active_daily.map((o) => o.resolved) },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Content>
  )
}
